import React, { useEffect, useState } from "react";
import { Star, X, ImagePlus } from "lucide-react";

interface ReviewModalProps {
  open: boolean;

  mode: "create" | "view" | "edit" | null;

  product: any;

  review?: {
    rating: number;
    comment: string;
    images: string[];
  };

  onClose: () => void;

  onSubmit: (
    rating: number,
    comment: string,
    images: (File | string)[]
  ) => void;

  // NEW
  onRatingChange?: (rating: number) => void;
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  open,
  mode,
  onClose,
  review,
  product,
  onSubmit,
  onRatingChange, // NEW
}) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [images, setImages] = useState<(File | string)[]>([]);

  useEffect(() => {
    if (!open) return;

    if (review) {
      setRating(review.rating);
      setReviewText(review.comment);
      setImages(review.images || []);
    } else {
      setRating(0);
      setReviewText("");
      setImages([]);
    }

    setHoverRating(0);
  }, [open, review]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white p-5">
          <div>
            <h2 className="text-xl font-bold text-[#2F5E2E]">
              {mode === "create"
                ? "Review Product"
                : mode === "edit"
                ? "Edit Review"
                : "Your Review"}
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              {mode === "view"
                ? "Your submitted review."
                : "Share your experience with other customers."}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-slate-100"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto space-y-6 p-6">

          {/* Product */}
          <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <img
              src={product?.image}
              alt={product?.productName || "Product"}
              className="h-20 w-20 rounded-xl border object-cover"
            />

            <div>
              <h3 className="font-semibold text-slate-800">
                {product?.productName}
              </h3>

              <p className="mt-1 text-sm text-slate-500">
                {product?.variantName}
              </p>
            </div>
          </div>

          {/* Rating */}
          <div>
            <p className="mb-3 font-semibold text-slate-700">
              Your Rating
            </p>

            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  disabled={mode === "view"}
                  onMouseEnter={() => {
                    if (mode === "view") return;
                    setHoverRating(star);
                  }}
                  onMouseLeave={() => {
                    if (mode === "view") return;
                    setHoverRating(0);
                  }}
                  onClick={() => {
                    if (mode === "view") return;

                    // Update local rating
                    setRating(star);

                    // IMPORTANT:
                    // Parent gets the rating immediately.
                    //
                    // 1,2,3 -> parent does nothing,
                    //         popup stays open.
                    //
                    // 4,5 -> parent opens Google Reviews
                    //        and closes this popup.
                    onRatingChange?.(star);
                  }}
                  className={`transition-transform ${
                    mode !== "view"
                      ? "hover:scale-110"
                      : "cursor-default"
                  }`}
                  aria-label={`Rate ${star} stars`}
                >
                  <Star
                    size={32}
                    className={
                      star <= (hoverRating || rating)
                        ? "fill-amber-400 text-amber-400"
                        : "text-slate-300"
                    }
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Review */}
          <div>
            <p className="mb-3 font-semibold text-slate-700">
              Your Review
            </p>

            <textarea
              readOnly={mode === "view"}
              rows={5}
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder={
                mode === "view"
                  ? ""
                  : "Tell us what you liked or disliked..."
              }
              className="w-full resize-none rounded-xl border border-slate-300 p-4 outline-none focus:border-[#6DBE45]"
            />
          </div>

          {/* Images */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-slate-700">
                Upload Images
              </p>

              <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">
                {images.length}/3
              </span>
            </div>

            {mode !== "view" && images.length < 3 && (
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-[#6DBE45] bg-[#F8FFF4] px-4 py-2 text-sm font-medium text-[#2F5E2E] hover:bg-[#EEF9E8]">
                <ImagePlus size={16} />

                <input
                  hidden
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    if (!e.target.files) return;

                    const selectedFiles = Array.from(
                      e.target.files
                    );

                    setImages((prev) => {
                      const remainingSlots = 3 - prev.length;

                      if (remainingSlots <= 0) {
                        return prev;
                      }

                      return [
                        ...prev,
                        ...selectedFiles.slice(
                          0,
                          remainingSlots
                        ),
                      ];
                    });

                    // Allow selecting the same image again
                    e.target.value = "";
                  }}
                />

                Add Image
              </label>
            )}

            {images.length > 0 && (
              <div className="flex flex-wrap gap-3">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className="relative"
                  >
                    <img
                      src={
                        typeof image === "string"
                          ? image
                          : URL.createObjectURL(image)
                      }
                      alt={`Review image ${index + 1}`}
                      className="h-24 w-24 rounded-xl border object-cover"
                    />

                    {mode !== "view" && (
                      <button
                        type="button"
                        onClick={() =>
                          setImages((prev) =>
                            prev.filter(
                              (_, i) => i !== index
                            )
                          )
                        }
                        className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white shadow hover:bg-red-600"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex justify-end gap-3 border-t bg-white p-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-5 py-2 hover:bg-slate-50"
          >
            {mode === "view" ? "Close" : "Cancel"}
          </button>

          {mode !== "view" && (
            <button
              type="button"
              onClick={() =>
                onSubmit(
                  rating,
                  reviewText,
                  images
                )
              }
              disabled={rating === 0}
              className="rounded-lg bg-[#2F5E2E] px-5 py-2 font-semibold text-white hover:bg-[#6DBE45] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mode === "edit"
                ? "Update Review"
                : "Submit Review"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;